// =============================================================================
// PricingPage.jsx
// =============================================================================
//
// Owner-only commercial config. Three live sections:
//   1. Customer discounts — percentage off retail fare
//   2. Postcode groups   — named buckets of UK postcode districts (collapsed)
//   3. Pricing rules     — flat-rate fares per route, four prices per row,
//                          applies to ALL by default with exclusion list, or
//                          to a specific allow-list (legacy mode).
//
// Plus two coming-soon placeholders (seasonal/event rates, surcharges).
//
// Schema notes (mig 044):
//   pricing_rules now stores ONE row per (route, dates) with four price
//   columns: price_business_pence, price_first_pence, price_suv_pence,
//   price_xl_pence. The applies_to_all BOOLEAN flag changes the meaning of
//   pricing_rule_customers rows:
//     true  → those customers are EXCLUDED from the rule
//     false → only those customers MATCH the rule (legacy allow-list)
// =============================================================================

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Tag, Plus, Edit2, Trash2, Search,
    MapPin, CalendarRange, BadgePercent, Route,
    ArrowRight, X, RotateCcw, ChevronDown, ChevronRight,
    TrendingUp,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Textarea, Select } from '../../components/ui/Form.jsx';
import { customerDisplayName } from '../../lib/customer.js';


// Vehicle class definitions — mirror backend enum order. The `key` matches
// the DB column suffix: price_{key}_pence.
const VEHICLE_CLASSES = [
    { key: 'business', label: 'Business Class' },
    { key: 'first',    label: 'First Class' },
    { key: 'suv',      label: 'First Class SUV' },
    { key: 'xl',       label: 'Business Class XL' },
];

// Day-of-week labels. Indexing matches JS Date.getDay() and the
// pricing_rule_time_windows.day_of_week column (0=Sun … 6=Sat).
// Order in this array is Mon-first for editor display purposes.
const DAYS = [
    { dow: 1, label: 'Mon', full: 'Monday' },
    { dow: 2, label: 'Tue', full: 'Tuesday' },
    { dow: 3, label: 'Wed', full: 'Wednesday' },
    { dow: 4, label: 'Thu', full: 'Thursday' },
    { dow: 5, label: 'Fri', full: 'Friday' },
    { dow: 6, label: 'Sat', full: 'Saturday' },
    { dow: 0, label: 'Sun', full: 'Sunday' },
];


// =============================================================================
// PricingPage — owner-only, redirects non-owners
// =============================================================================
export function PricingPage() {
    const { isOwner } = useCurrentUser();

    if (!isOwner) {
        return (
            <div className="p-6 max-w-2xl mx-auto text-sm text-ink-subtle">
                Pricing is owner-only.
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                title="Pricing"
                subtitle="Commercial terms and rules applied to bookings."
            />

            <div className="space-y-5">
                <DiscountsSection />
                <PostcodeGroupsSection />
                <PricingRulesSection />
                <DateSurchargesSection />
            </div>
        </div>
    );
}


// =============================================================================
// DiscountsSection — unchanged from previous version
// =============================================================================
function DiscountsSection() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['pricing', 'discounts'],
        queryFn: () => api.get('/pricing/admin/discounts'),
    });

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['pricing', 'discounts'] });
    }

    const discounts = data?.discounts || [];

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Tag className="h-4 w-4 text-ink-subtle" />
                        Customer discounts
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Percentage off retail fare. Applied automatically to every booking.
                    </p>
                </div>
                <Button size="sm" onClick={() => setShowAddModal(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    Add discount
                </Button>
            </div>

            {isLoading && <div className="card-body text-sm text-ink-subtle text-center py-6">Loading discounts…</div>}
            {isError && <div className="card-body"><ErrorPage message={error?.message} onRetry={refetch} /></div>}
            {!isLoading && !isError && discounts.length === 0 && (
                <div className="card-body text-sm text-ink-subtle text-center py-6">
                    No discounts set yet. Add one to give a customer a percentage off retail.
                </div>
            )}
            {!isLoading && !isError && discounts.length > 0 && (
                <ul className="divide-y divide-paper-border-soft">
                  {discounts.map((d) => (
                        <li key={d.id} className="px-6 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink truncate">
                                    {customerDisplayName({
                                        company_name: d.company_name,
                                        first_name: d.first_name,
                                        last_name: d.last_name,
                                    })}
                                </div>
                                <div className="text-xs text-ink-subtle">
                                    {Number(d.discount_pct).toFixed(2)}% off retail
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <button type="button" onClick={() => setEditTarget(d)} className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors" title="Edit">
                                    <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button type="button" onClick={() => setDeleteTarget(d)} className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors" title="Delete">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {showAddModal && (
                <DiscountModal mode="add" open onClose={() => setShowAddModal(false)} onSaved={handleChanged} />
            )}
            {editTarget && (
                <DiscountModal mode="edit" discount={editTarget} open onClose={() => setEditTarget(null)} onSaved={handleChanged} />
            )}
            {deleteTarget && (
                <DeleteDiscountModal discount={deleteTarget} open onClose={() => setDeleteTarget(null)} onDeleted={handleChanged} />
            )}
        </div>
    );
}


function DiscountModal({ mode, discount, open, onClose, onSaved }) {
    const api = useApi();
const [customer, setCustomer] = useState(
        discount
            ? { id: discount.customer_id, company_name: discount.company_name, first_name: discount.first_name, last_name: discount.last_name }
            : null
    );
    const [pct, setPct] = useState(discount ? String(discount.discount_pct) : '');
    const [pctError, setPctError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => {
            const body = { customer_id: customer.id, discount_pct: parseFloat(pct) };
            if (mode === 'add') return api.post('/pricing/admin/discounts', body);
            return api.patch(`/pricing/admin/discounts/${discount.id}`, { discount_pct: parseFloat(pct) });
        },
        onSuccess: () => {
            toast.success(mode === 'add' ? 'Discount added' : 'Discount updated');
            onSaved();
        },
        onError: (err) => toast.error(err.message || 'Could not save discount'),
    });

    function handleSave() {
        setPctError(null);
        if (!customer) { toast.error('Pick a customer'); return; }
        const n = parseFloat(pct);
        if (isNaN(n) || n <= 0 || n > 100) { setPctError('Enter a percentage between 0 and 100'); return; }
        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={mode === 'add' ? 'Add customer discount' : 'Edit discount'}
            description="Percentage off retail fare. Applies to base fare ex VAT only; surcharges and VAT pass through."
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">Customer</label>
                    {mode === 'edit' ? (
                        <div className="text-sm text-ink">
                            {customerDisplayName({
                                company_name: customer.company_name,
                                first_name: customer.first_name,
                                last_name: customer.last_name,
                            })}
                        </div>
                    ) : (
                        <CustomerSinglePicker selected={customer} onChange={setCustomer} />
                    )}
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">Discount %</label>
                    <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="100"
                        value={pct}
                        onChange={(e) => setPct(e.target.value)}
                        placeholder="e.g. 10.00"
                    />
                    {pctError && <p className="mt-1 text-xs text-status-danger">{pctError}</p>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    {mode === 'add' ? 'Add discount' : 'Save changes'}
                </Button>
            </div>
        </Modal>
    );
}


function DeleteDiscountModal({ discount, open, onClose, onDeleted }) {
    const api = useApi();
const mutation = useMutation({
        mutationFn: () => api.delete(`/pricing/admin/discounts/${discount.id}`),
        onSuccess: () => { toast.success('Discount removed'); onDeleted(); },
        onError: (err) => toast.error(err.message || 'Could not remove discount'),
    });

    return (
        <Modal open={open} onClose={onClose} title="Remove discount?" description="Future bookings will charge retail fare." size="md">
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm">
                <div className="font-medium text-ink">
                    {customerDisplayName({
                        company_name: discount.company_name,
                        first_name: discount.first_name,
                        last_name: discount.last_name,
                    })}
                </div>
                <div className="text-ink-muted">{Number(discount.discount_pct).toFixed(2)}% off retail</div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" /> Remove
                </Button>
            </div>
        </Modal>
    );
}


// =============================================================================
// PostcodeGroupsSection — collapsed by default, one-line summary
// =============================================================================
function PostcodeGroupsSection() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['pricing', 'postcode-groups'],
        queryFn: () => api.get('/pricing/admin/postcode-groups'),
    });

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['pricing', 'postcode-groups'] });
    }

    const groups = data?.groups || [];

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-start gap-2 text-left flex-1 min-w-0 group"
                >
                    {expanded
                        ? <ChevronDown className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        : <ChevronRight className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-ink-subtle" />
                            Postcode groups
                            <span className="text-xs font-normal text-ink-subtle">
                                ({groups.length})
                            </span>
                        </h2>
                        <p className="text-xs text-ink-subtle mt-0.5 truncate">
                            {expanded
                                ? 'Named buckets of UK postcode districts (W1, SW1A, TW6…). Used by pricing rules.'
                                : groups.length === 0
                                    ? 'No groups yet — click to expand.'
                                    : groups.map(g => g.name).join(' · ')}
                        </p>
                    </div>
                </button>
                {expanded && (
                    <Button size="sm" onClick={() => setShowAddModal(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        Add group
                    </Button>
                )}
            </div>

            {expanded && (
                <>
                    {isLoading && <div className="card-body text-sm text-ink-subtle text-center py-6">Loading groups…</div>}
                    {isError && <div className="card-body"><ErrorPage message={error?.message} onRetry={refetch} /></div>}
                    {!isLoading && !isError && groups.length === 0 && (
                        <div className="card-body text-sm text-ink-subtle text-center py-6">
                            No groups yet. Add one to start defining route-based pricing rules.
                        </div>
                    )}
                    {!isLoading && !isError && groups.length > 0 && (
                        <ul className="divide-y divide-paper-border-soft">
                            {groups.map((g) => (
                                <PostcodeGroupRow
                                    key={g.id}
                                    group={g}
                                    onEdit={() => setEditTarget(g)}
                                    onDelete={() => setDeleteTarget(g)}
                                />
                            ))}
                        </ul>
                    )}
                </>
            )}

            {showAddModal && (
                <PostcodeGroupModal mode="add" open onClose={() => setShowAddModal(false)} onSaved={handleChanged} />
            )}
            {editTarget && (
                <PostcodeGroupModal mode="edit" group={editTarget} open onClose={() => setEditTarget(null)} onSaved={handleChanged} />
            )}
            {deleteTarget && (
                <DeletePostcodeGroupModal group={deleteTarget} open onClose={() => setDeleteTarget(null)} onDeleted={handleChanged} />
            )}
        </div>
    );
}


function PostcodeGroupRow({ group, onEdit, onDelete }) {
    // Backend may return either strings or {id, district} objects — normalise.
    const districts = (group.districts || []).map(d => typeof d === 'string' ? d : d.district);
    const preview = districts.slice(0, 30);
    const remaining = districts.length - preview.length;

    return (
        <li className="px-6 py-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{group.name}</div>
                    {group.description && (
                        <div className="text-xs text-ink-subtle mt-0.5">{group.description}</div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                        {preview.map((d) => (
                            <span key={d} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-paper-hover text-ink">
                                {d}
                            </span>
                        ))}
                        {remaining > 0 && (
                            <span className="text-xs text-ink-subtle self-center"> + {remaining} more</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={onEdit} className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors" title="Edit">
                        <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={onDelete} className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </li>
    );
}


function PostcodeGroupModal({ mode, group, open, onClose, onSaved }) {
    const api = useApi();
    const [name, setName] = useState(group?.name || '');
    const [description, setDescription] = useState(group?.description || '');
    const [districtsText, setDistrictsText] = useState(() => {
        if (!group?.districts) return '';
        return group.districts.map(d => typeof d === 'string' ? d : d.district).join(', ');
    });

    const mutation = useMutation({
        mutationFn: () => {
            const districts = districtsText
                .split(/[\s,\n]+/)
                .map(s => s.trim().toUpperCase())
                .filter(Boolean);
            const body = { name: name.trim(), description: description.trim() || null, districts };
            if (mode === 'add') return api.post('/pricing/admin/postcode-groups', body);
            return api.patch(`/pricing/admin/postcode-groups/${group.id}`, body);
        },
        onSuccess: () => {
            toast.success(mode === 'add' ? 'Group created' : 'Group updated');
            onSaved();
        },
        onError: (err) => toast.error(err.message || 'Could not save group'),
    });

    function handleSave() {
        if (!name.trim()) { toast.error('Name is required'); return; }
        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={mode === 'add' ? 'Add postcode group' : 'Edit postcode group'}
            description='Districts are the part before the space — e.g. W1, SW1A, EC2N, TW6.'
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Name <span className="text-status-danger">*</span>
                    </label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. London" />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">Description</label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Optional — e.g. "Central London postcodes"'
                    />
                </div>
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">Districts</label>
                    <Textarea
                        rows={6}
                        value={districtsText}
                        onChange={(e) => setDistrictsText(e.target.value)}
                        placeholder="W1, SW1A, EC2N, TW6…"
                    />
                    <p className="mt-1 text-xs text-ink-subtle">
                        Paste districts separated by commas, spaces, or newlines. Each district can only belong to one group.
                    </p>
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    {mode === 'add' ? 'Create' : 'Save changes'}
                </Button>
            </div>
        </Modal>
    );
}


function DeletePostcodeGroupModal({ group, open, onClose, onDeleted }) {
    const api = useApi();
    const mutation = useMutation({
        mutationFn: () => api.delete(`/pricing/admin/postcode-groups/${group.id}`),
        onSuccess: () => { toast.success('Group deleted'); onDeleted(); },
        onError: (err) => toast.error(err.message || 'Could not delete group'),
    });

    return (
        <Modal open={open} onClose={onClose} title="Delete postcode group?" description="Cannot be undone. Any rules using this group must be removed first." size="md">
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm">
                <div className="font-medium text-ink">{group.name}</div>
                <div className="text-ink-muted">{(group.districts || []).length} districts</div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" /> Delete
                </Button>
            </div>
        </Modal>
    );
}


// =============================================================================
// PricingRulesSection — mig 044 schema, four prices per row, applies_to_all flag
// =============================================================================
function PricingRulesSection() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['pricing', 'rules'],
        queryFn: () => api.get('/pricing/admin/rules'),
    });
    const { data: groupsData } = useQuery({
        queryKey: ['pricing', 'postcode-groups'],
        queryFn: () => api.get('/pricing/admin/postcode-groups'),
    });

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
    }

    const rules = data?.rules || [];
    const groups = groupsData?.groups || [];
    const canAdd = groups.length >= 2;

return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-start gap-2 text-left flex-1 min-w-0 group"
                >
                    {expanded
                        ? <ChevronDown className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        : <ChevronRight className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                            <Route className="h-4 w-4 text-ink-subtle" />
                            Pricing rules
                            <span className="text-xs font-normal text-ink-subtle">
                                ({rules.length})
                            </span>
                        </h2>
                        <p className="text-xs text-ink-subtle mt-0.5 truncate">
                            {expanded
                                ? 'Flat-rate fares for specific routes. Replaces distance pricing on the fare only — surcharges and VAT still apply on top.'
                                : rules.length === 0
                                    ? 'No rules yet — click to expand.'
                                    : rules.map(r => `${r.origin_group_name} → ${r.destination_group_name}`).join(' · ')}
                        </p>
                    </div>
                </button>
                {expanded && (
                    <Button size="sm" onClick={() => setShowAddModal(true)} disabled={!canAdd}>
                        <Plus className="h-3.5 w-3.5" />
                        Add rule
                    </Button>
                )}
            </div>

            {expanded && (
                <>
                    {isLoading && <div className="card-body text-sm text-ink-subtle text-center py-6">Loading rules…</div>}
                    {isError && <div className="card-body"><ErrorPage message={error?.message} onRetry={refetch} /></div>}
                    {!isLoading && !isError && rules.length === 0 && (
                        <div className="card-body text-sm text-ink-subtle text-center py-6">
                            {!canAdd
                                ? 'Create at least two postcode groups above to start adding pricing rules.'
                                : 'No rules yet. Add one to set a flat fare for a route.'}
                        </div>
                    )}
                    {!isLoading && !isError && rules.length > 0 && (
                        <ul className="divide-y divide-paper-border-soft">
                            {rules.map((r) => (
                                <PricingRuleRow
                                    key={r.id}
                                    rule={r}
                                    onEdit={() => setEditTarget(r)}
                                    onDelete={() => setDeleteTarget(r)}
                                />
                            ))}
                        </ul>
                    )}
                </>
            )}

            {showAddModal && (
                <PricingRuleModal
                    mode="add"
                    groups={groups}
                    open
                    onClose={() => setShowAddModal(false)}
                    onSaved={handleChanged}
                />
            )}
            {editTarget && (
                <PricingRuleModal
                    mode="edit"
                    rule={editTarget}
                    groups={groups}
                    open
                    onClose={() => setEditTarget(null)}
                    onSaved={handleChanged}
                />
            )}
            {deleteTarget && (
                <DeletePricingRuleModal
                    rule={deleteTarget}
                    open
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={handleChanged}
                />
            )}
        </div>
    );
}


// -----------------------------------------------------------------------------
// PricingRuleRow — vertical layout: route line, 4-price row, dates+scope line
// -----------------------------------------------------------------------------
function PricingRuleRow({ rule, onEdit, onDelete }) {
    const customers = rule.customers || [];
    const appliesToAll = rule.applies_to_all !== false;

    // Scope line. Three states:
    //   1. applies_to_all=true, no exclusions → "All customers"
    //   2. applies_to_all=true, with exclusions → "All customers except X, Y, Z"
    //   3. applies_to_all=false → "Only X, Y, Z" (legacy allow-list)
    function renderScope() {
        if (appliesToAll && customers.length === 0) {
            return <span className="text-ink-subtle">All customers</span>;
        }
        const names = customers.map((c) =>
            customerDisplayName({
                company_name: c.company_name,
                first_name: c.first_name,
                last_name: c.last_name,
            })
        );
        const shown = names.slice(0, 4);
        const overflow = names.length - shown.length;
        return (
            <span>
                <span className="text-ink-subtle">
                    {appliesToAll ? 'All customers except ' : 'Only '}
                </span>
                {shown.map((n, i) => (
                    <span key={i}>
                        {i > 0 && ', '}
                        <span className="text-ink">{n}</span>
                    </span>
                ))}
                {overflow > 0 && <span className="text-ink-subtle"> + {overflow} more</span>}
            </span>
        );
    }

    return (
        <li className="px-6 py-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    {/* Route line */}
                    <div className="flex items-center gap-2 text-sm font-medium text-ink flex-wrap">
                        <span>{rule.origin_group_name}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-ink-subtle" />
                        <span>{rule.destination_group_name}</span>
                        {!rule.active && <StatusPill status="neutral">Inactive</StatusPill>}
                        {rule.apply_to_booking_engine && <StatusPill status="info">Public</StatusPill>}
                    </div>

                    {/* 4-price row */}
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {VEHICLE_CLASSES.map((vc) => {
                            const pence = rule[`price_${vc.key}_pence`];
                            const set = pence != null && pence !== '';
                            return (
                                <div
                                    key={vc.key}
                                    className={`rounded-md px-2.5 py-1.5 border ${
                                        set
                                            ? 'border-paper-border-soft bg-paper-hover/40'
                                            : 'border-dashed border-paper-border-soft/70 bg-transparent'
                                    }`}
                                >
                                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
                                        {vc.label}
                                    </div>
                                    <div className={`text-sm font-semibold ${set ? 'text-ink' : 'text-ink-subtle/60'}`}>
                                        {set ? `£${(Number(pence) / 100).toFixed(2)}` : '—'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Dates + scope line */}
                    <div className="mt-2 text-xs text-ink-subtle">
                        From {formatDate(rule.effective_from)}
                        {rule.effective_until ? ` until ${formatDate(rule.effective_until)}` : ' (open-ended)'}
                        {' · '}
                        {renderScope()}
                    </div>

                    {/* Operating hours (only render if any) */}
                    {Array.isArray(rule.time_windows) && rule.time_windows.length > 0 && (
                        <div className="mt-1 text-xs text-ink-subtle">
                            {formatTimeWindows(rule.time_windows)}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={onEdit} className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors" title="Edit">
                        <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={onDelete} className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </li>
    );
}


// -----------------------------------------------------------------------------
// PricingRuleModal — add or edit. Four price inputs in a grid,
// applies_to_all toggle, customer picker labelled per mode.
// -----------------------------------------------------------------------------
function PricingRuleModal({ mode, rule, groups, open, onClose, onSaved }) {
    const api = useApi();

    const [originId, setOriginId] = useState(rule?.origin_group_id || '');
    const [destId, setDestId] = useState(rule?.destination_group_id || '');

    // Four price strings (pounds, free text — converted to pence on save).
    function penceToPoundsStr(p) {
        return p != null && p !== '' ? (Number(p) / 100).toFixed(2) : '';
    }
    const [prices, setPrices] = useState({
        business: penceToPoundsStr(rule?.price_business_pence),
        first:    penceToPoundsStr(rule?.price_first_pence),
        suv:      penceToPoundsStr(rule?.price_suv_pence),
        xl:       penceToPoundsStr(rule?.price_xl_pence),
    });

    const [effectiveFrom, setEffectiveFrom] = useState(
        rule?.effective_from
            ? new Date(rule.effective_from).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10)
    );
    const [effectiveUntil, setEffectiveUntil] = useState(
        rule?.effective_until
            ? new Date(rule.effective_until).toISOString().slice(0, 10)
            : ''
    );
    const [active, setActive] = useState(rule?.active !== false);
    const [createReverse, setCreateReverse] = useState(false);
   const [appliesToAll, setAppliesToAll] = useState(
        rule?.applies_to_all !== undefined ? rule.applies_to_all : true
    );
    const [applyToBookingEngine, setApplyToBookingEngine] = useState(
        rule?.apply_to_booking_engine === true
    );

    // Time windows. Stored as a {0..6 → {time_from, time_until} | null} map.
    // null means "day not active". Initialised from rule.time_windows if editing.
    const [timeWindows, setTimeWindows] = useState(() => {
        const initial = { 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null };
        for (const w of (rule?.time_windows || [])) {
            initial[w.day_of_week] = { time_from: w.time_from, time_until: w.time_until };
        }
        return initial;
    });

    const [selectedCustomers, setSelectedCustomers] = useState(() =>
        (rule?.customers || []).map((c) => ({
            id: c.customer_id,
            company_name: c.company_name,
            first_name: c.first_name,
            last_name: c.last_name,
        }))
    );

    const [priceError, setPriceError] = useState(null);
    const [dateError, setDateError] = useState(null);
    const [routeError, setRouteError] = useState(null);

    function poundsToPenceOrNull(s) {
        const trimmed = String(s).trim();
        if (trimmed === '') return null;
        const n = parseFloat(trimmed);
        if (isNaN(n) || n <= 0) return 'INVALID';
        return Math.round(n * 100);
    }

    function buildPricesPayload() {
        const out = {};
        for (const key of ['business', 'first', 'suv', 'xl']) {
            const v = poundsToPenceOrNull(prices[key]);
            if (v === 'INVALID') return { error: `Enter a valid price for ${VEHICLE_CLASSES.find(c => c.key === key).label}, or leave blank.` };
            out[key] = v;
        }
        const hasAny = Object.values(out).some((v) => v != null);
        if (!hasAny) return { error: 'Set at least one price.' };
        return { prices: out };
    }

function buildTimeWindowsPayload() {
        const out = [];
        for (const dow of [0, 1, 2, 3, 4, 5, 6]) {
            const w = timeWindows[dow];
            if (!w) continue;
            if (!/^\d{2}:\d{2}$/.test(w.time_from) || !/^\d{2}:\d{2}$/.test(w.time_until)) {
                const day = DAYS.find((d) => d.dow === dow);
                return { error: `Set valid times on ${day.full}.` };
            }
            if (w.time_until <= w.time_from) {
                const day = DAYS.find((d) => d.dow === dow);
                return { error: `${day.full}: end time must be after start time.` };
            }
            out.push({ day_of_week: dow, time_from: w.time_from, time_until: w.time_until });
        }
        return { windows: out };
    }

    const mutation = useMutation({
        mutationFn: () => {
            const built = buildPricesPayload();
            if (built.error) throw new Error(built.error);
            const tw = buildTimeWindowsPayload();
            if (tw.error) throw new Error(tw.error);
            const customerIds = selectedCustomers.map((c) => c.id);

                if (mode === 'add') {
                if (!originId || !destId) throw new Error('Pick both origin and destination groups.');
                return api.post('/pricing/admin/rules', {
                    origin_group_id: originId,
                    destination_group_id: destId,
                    prices: built.prices,
                    effective_from: effectiveFrom,
                    effective_until: effectiveUntil || null,
                    active,
                    applies_to_all: appliesToAll,
                    customer_ids: customerIds,
                    create_reverse: createReverse,
                    apply_to_booking_engine: applyToBookingEngine,
                    time_windows: tw.windows,
                });
            }
           return api.patch(`/pricing/admin/rules/${rule.id}`, {
                prices: built.prices,
                effective_from: effectiveFrom,
                effective_until: effectiveUntil || null,
                active,
                applies_to_all: appliesToAll,
                customer_ids: customerIds,
                apply_to_booking_engine: applyToBookingEngine,
                time_windows: tw.windows,
            });
        },
        onSuccess: () => {
            toast.success(mode === 'add' ? 'Rule created' : 'Rule updated');
            onSaved();
        },
        onError: (err) => {
            const apiErr = err?.response?.error || err?.body?.error;
            if (apiErr === 'overlapping_rule') {
                const msg = err?.response?.message || err?.body?.message;
                setDateError(msg || 'A rule for this route already covers part of this date range.');
                return;
            }
            toast.error(err.message || 'Could not save rule');
        },
    });

    function handleSave() {
        setPriceError(null);
        setDateError(null);
        setRouteError(null);

        const built = buildPricesPayload();
        if (built.error) { setPriceError(built.error); return; }

       if (mode === 'add') {
            if (!originId || !destId) { setRouteError('Pick both origin and destination groups.'); return; }
        }
        if (!effectiveFrom) { setDateError('Set an effective-from date.'); return; }

        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={mode === 'add' ? 'Add pricing rule' : 'Edit pricing rule'}
            description="Flat fare ex VAT. Surcharges + VAT still apply on top."
            size="lg"
        >
            <div className="space-y-4">
                {/* Route */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            From <span className="text-status-danger">*</span>
                        </label>
                        <Select
                            value={originId}
                            onChange={(e) => setOriginId(e.target.value)}
                            disabled={mode === 'edit'}
                        >
                            <option value="">Select group…</option>
                            {groups.map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            To <span className="text-status-danger">*</span>
                        </label>
                        <Select
                            value={destId}
                            onChange={(e) => setDestId(e.target.value)}
                            disabled={mode === 'edit'}
                        >
                            <option value="">Select group…</option>
                            {groups.map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </Select>
                    </div>
                </div>
                {routeError && <p className="text-xs text-status-danger -mt-2">{routeError}</p>}

                {/* Four prices in a grid */}
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Flat fare £ (ex VAT) <span className="text-status-danger">*</span>
                    </label>
                    <p className="text-xs text-ink-subtle mb-2">
                        Enter prices for the vehicle classes you want covered. Leave blank to fall back to distance pricing for that class.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {VEHICLE_CLASSES.map((vc) => (
                            <div key={vc.key}>
                                <div className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium mb-1">
                                    {vc.label}
                                </div>
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-subtle text-sm pointer-events-none">£</span>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={prices[vc.key]}
                                        onChange={(e) => setPrices((p) => ({ ...p, [vc.key]: e.target.value }))}
                                        placeholder="—"
                                        className="pl-5"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {priceError && <p className="mt-1 text-xs text-status-danger">{priceError}</p>}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Effective from <span className="text-status-danger">*</span>
                        </label>
                        <Input
                            type="date"
                            value={effectiveFrom}
                            onChange={(e) => setEffectiveFrom(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Effective until
                        </label>
                        <Input
                            type="date"
                            value={effectiveUntil}
                            onChange={(e) => setEffectiveUntil(e.target.value)}
                        />
                        <p className="mt-1 text-xs text-ink-subtle">Leave blank for open-ended.</p>
                    </div>
                </div>
                {dateError && <p className="text-xs text-status-danger -mt-2">{dateError}</p>}

                {/* Active */}
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Active</div>
                        <div className="text-xs text-ink-subtle">
                            Uncheck to disable without deleting. Inactive rules never fire.
                        </div>
                    </div>
                </label>

               {/* Apply to booking engine */}
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={applyToBookingEngine}
                        onChange={(e) => setApplyToBookingEngine(e.target.checked)}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Apply to booking engine</div>
                        <div className="text-xs text-ink-subtle">
                            When on, this rule also fires for anonymous users on book.lanzcts.com. Customer include/exclude lists are ignored in that case (the public has no customer record).
                        </div>
                    </div>
                </label>

                {/* Operating hours (optional) */}
                <TimeWindowsEditor windows={timeWindows} onChange={setTimeWindows} />

                {/* Reverse rule (add mode only, and only when origin ≠ destination) */}
                {mode === 'add' && originId && destId && originId !== destId && (
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={createReverse}
                            onChange={(e) => setCreateReverse(e.target.checked)}
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-sm text-ink flex items-center gap-1.5">
                                <RotateCcw className="h-3.5 w-3.5 text-ink-subtle" />
                                Also create the reverse rule
                            </div>
                            <div className="text-xs text-ink-subtle">
                                Mirrors origin ↔ destination with the same prices, dates, scope, and customers.
                            </div>
                        </div>
                    </label>
                )}

                {/* Scope toggle + customer picker */}
                <div className="pt-3 border-t border-paper-border-soft space-y-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Who this rule applies to
                        </label>
                        <div className="space-y-1.5">
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="applies_to_all"
                                    checked={appliesToAll}
                                    onChange={() => setAppliesToAll(true)}
                                    className="mt-0.5"
                                />
                                <div>
                                    <div className="text-sm text-ink">All customers</div>
                                    <div className="text-xs text-ink-subtle">
                                        Default behaviour. Optionally exclude specific customers below.
                                    </div>
                                </div>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="applies_to_all"
                                    checked={!appliesToAll}
                                    onChange={() => setAppliesToAll(false)}
                                    className="mt-0.5"
                                />
                                <div>
                                    <div className="text-sm text-ink">Only specific customers</div>
                                    <div className="text-xs text-ink-subtle">
                                        Legacy allow-list. Rule only fires for customers added below.
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            {appliesToAll ? 'Customers to exclude' : 'Customers to include'}
                        </label>
                        <p className="text-xs text-ink-subtle mb-2">
                            {appliesToAll
                                ? 'Leave empty to apply to absolutely everyone. Add customers here to opt them out.'
                                : 'Add at least one customer. The rule will only fire for these.'}
                        </p>
                        <CustomerMultiPicker
                            selected={selectedCustomers}
                            onChange={setSelectedCustomers}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    {mode === 'add' ? <><Plus className="h-4 w-4" />Create</> : 'Save changes'}
                </Button>
            </div>
        </Modal>
    );
}


function TimeWindowsEditor({ windows, onChange }) {
    const anyActive = Object.values(windows).some((w) => w);

    function toggleDay(dow, checked) {
        onChange({
            ...windows,
            [dow]: checked ? { time_from: '07:00', time_until: '19:00' } : null,
        });
    }
    function updateTime(dow, field, value) {
        onChange({
            ...windows,
            [dow]: { ...(windows[dow] || { time_from: '07:00', time_until: '19:00' }), [field]: value },
        });
    }
    function applyAllDays() {
        const next = { ...windows };
        // Use Monday's window as the template if defined, else default.
        const template = windows[1] || { time_from: '07:00', time_until: '19:00' };
        for (const { dow } of DAYS) {
            next[dow] = { ...template };
        }
        onChange(next);
    }
    function applyMonToFri() {
        const next = { ...windows };
        const template = windows[1] || { time_from: '07:00', time_until: '19:00' };
        for (const dow of [1, 2, 3, 4, 5]) {
            next[dow] = { ...template };
        }
        onChange(next);
    }
    function clearAll() {
        onChange({ 0: null, 1: null, 2: null, 3: null, 4: null, 5: null, 6: null });
    }

    return (
        <div className="pt-3 border-t border-paper-border-soft">
            <div className="flex items-center justify-between gap-2 mb-2">
                <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium">
                    Operating hours (optional)
                </label>
                <div className="flex items-center gap-2 text-xs">
                    <button type="button" onClick={applyMonToFri} className="text-ink-subtle hover:text-ink underline">
                        Mon–Fri
                    </button>
                    <span className="text-paper-border-soft">·</span>
                    <button type="button" onClick={applyAllDays} className="text-ink-subtle hover:text-ink underline">
                        All days
                    </button>
                    {anyActive && (
                        <>
                            <span className="text-paper-border-soft">·</span>
                            <button type="button" onClick={clearAll} className="text-ink-subtle hover:text-status-danger underline">
                                Clear
                            </button>
                        </>
                    )}
                </div>
            </div>
            <p className="text-xs text-ink-subtle mb-2">
                Leave all days unticked for no time restriction. Pickup time is what's checked — drop-off time is irrelevant.
            </p>
            <div className="space-y-1.5">
                {DAYS.map(({ dow, label }) => {
                    const w = windows[dow];
                    return (
                        <div key={dow} className="flex items-center gap-2">
                            <label className="flex items-center gap-2 cursor-pointer w-20">
                                <input
                                    type="checkbox"
                                    checked={!!w}
                                    onChange={(e) => toggleDay(dow, e.target.checked)}
                                />
                                <span className="text-sm text-ink">{label}</span>
                            </label>
                            <Input
                                type="time"
                                value={w?.time_from || ''}
                                onChange={(e) => updateTime(dow, 'time_from', e.target.value)}
                                disabled={!w}
                                className="w-28"
                            />
                            <span className="text-ink-subtle text-xs">→</span>
                            <Input
                                type="time"
                                value={w?.time_until || ''}
                                onChange={(e) => updateTime(dow, 'time_until', e.target.value)}
                                disabled={!w}
                                className="w-28"
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function DeletePricingRuleModal({ rule, open, onClose, onDeleted }) {
    const api = useApi();
    const mutation = useMutation({
        mutationFn: () => api.delete(`/pricing/admin/rules/${rule.id}`),
        onSuccess: () => { toast.success('Rule deleted'); onDeleted(); },
        onError: (err) => toast.error(err.message || 'Could not delete rule'),
    });

    // Build a price summary for the confirmation card.
    const priceParts = VEHICLE_CLASSES
        .map((vc) => {
            const p = rule[`price_${vc.key}_pence`];
            if (p == null || p === '') return null;
            return `${vc.label} £${(Number(p) / 100).toFixed(2)}`;
        })
        .filter(Boolean);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete pricing rule?"
            description="The flat-rate fare for this route will no longer apply. Distance pricing returns for the affected customers."
            size="md"
        >
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm text-ink-muted">
                <div className="font-medium text-ink">
                    {rule.origin_group_name} → {rule.destination_group_name}
                </div>
                {priceParts.length > 0 && (
                    <div className="mt-0.5 text-xs">{priceParts.join(' · ')}</div>
                )}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" /> Delete
                </Button>
            </div>
        </Modal>
    );
}


// =============================================================================
// CustomerMultiPicker — search and add multiple, shows pills with X to remove
// =============================================================================
function CustomerMultiPicker({ selected, onChange }) {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 200);
        return () => clearTimeout(t);
    }, [query]);

    const { data, isFetching } = useQuery({
        queryKey: ['customers', 'search', debounced],
        queryFn: () => api.get('/customers', { query: { search: debounced, limit: 10 } }),
        enabled: debounced.length > 0,
    });

    const results = (data?.customers || []).filter(
        (c) => !selected.find((s) => s.id === c.id)
    );

    function addCustomer(c) {
        onChange([...selected, {
            id: c.id,
            company_name: c.company_name,
            first_name: c.first_name,
            last_name: c.last_name,
        }]);
        setQuery('');
    }

    function removeCustomer(id) {
        onChange(selected.filter((c) => c.id !== id));
    }

    return (
        <div>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {selected.map((c) => (
                        <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-paper-hover text-ink">
                            {customerDisplayName({
                                company_name: c.company_name,
                                first_name: c.first_name,
                                last_name: c.last_name,
                            })}
                            <button
                                type="button"
                                onClick={() => removeCustomer(c.id)}
                                className="text-ink-subtle hover:text-status-danger"
                                title="Remove"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle pointer-events-none" />
                <Input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 200)}
                    placeholder="Search customer by name, company, or email…"
                    className="pl-8"
                />
                {open && debounced.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-paper border border-paper-border-soft rounded-md shadow-md max-h-60 overflow-y-auto z-10">
                        {isFetching && (
                            <div className="px-3 py-2 text-xs text-ink-subtle">Searching…</div>
                        )}
                        {!isFetching && results.length === 0 && (
                            <div className="px-3 py-2 text-xs text-ink-subtle">No matches</div>
                        )}
                        {!isFetching && results.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => addCustomer(c)}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-paper-hover/60 transition-colors"
                            >
                                {customerDisplayName({
                                    company_name: c.company_name,
                                    first_name: c.first_name,
                                    last_name: c.last_name,
                                })}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function CustomerSinglePicker({ selected, onChange }) {
    const api = useApi();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 200);
        return () => clearTimeout(t);
    }, [query]);

    const { data, isFetching } = useQuery({
        queryKey: ['customers', 'search', debounced],
        queryFn: () => api.get('/customers', { query: { search: debounced, limit: 10 } }),
        enabled: debounced.length > 0,
    });

    if (selected) {
        return (
            <div className="flex items-center justify-between gap-2 rounded-md border border-paper-border-soft bg-paper-hover/40 px-3 py-2">
                <span className="text-sm text-ink">
                    {customerDisplayName({
                        company_name: selected.company_name,
                        first_name: selected.first_name,
                        last_name: selected.last_name,
                    })}
                </span>
                <button
                    type="button"
                    onClick={() => onChange(null)}
                    className="text-ink-subtle hover:text-status-danger"
                    title="Change"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        );
    }

    return (
        <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-subtle pointer-events-none" />
            <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 200)}
                placeholder="Search customer by name, company, or email…"
                className="pl-8"
            />
            {open && debounced.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-paper border border-paper-border-soft rounded-md shadow-md max-h-60 overflow-y-auto z-10">
                    {isFetching && (
                        <div className="px-3 py-2 text-xs text-ink-subtle">Searching…</div>
                    )}
                    {!isFetching && (data?.customers || []).length === 0 && (
                        <div className="px-3 py-2 text-xs text-ink-subtle">No matches</div>
                    )}
                    {!isFetching && (data?.customers || []).map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => onChange(c)}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-paper-hover/60 transition-colors"
                        >
                            {customerDisplayName({
                                company_name: c.company_name,
                                first_name: c.first_name,
                                last_name: c.last_name,
                            })}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


// =============================================================================
// ComingSoonSection — placeholder card
// =============================================================================
function ComingSoonSection({ title, icon: Icon, description }) {
    return (
        <div className="card opacity-60">
            <div className="card-header flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                        <Icon className="h-4 w-4 text-ink-subtle" />
                        {title}
                    </h2>
                    <p className="text-xs text-ink-subtle mt-0.5">{description}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-ink-subtle font-medium">
                    Coming soon
                </span>
            </div>
        </div>
    );
}


// =============================================================================
// DateSurchargesSection — collapsed by default, date-based % multipliers
// =============================================================================
// Surcharge multiplies the BASE FARE EX VAT, applied AFTER discount/flat-rate.
// Single date per row (UNIQUE constraint). applies_to_all + exclusion list
// follows the same model as pricing rules.
function DateSurchargesSection() {
    const api = useApi();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['pricing', 'surcharges'],
        queryFn: () => api.get('/pricing/admin/surcharges'),
    });

    function handleChanged() {
        setShowAddModal(false);
        setEditTarget(null);
        setDeleteTarget(null);
        queryClient.invalidateQueries({ queryKey: ['pricing', 'surcharges'] });
    }

    const surcharges = data?.surcharges || [];

    return (
        <div className="card">
            <div className="card-header flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-start gap-2 text-left flex-1 min-w-0 group"
                >
                    {expanded
                        ? <ChevronDown className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        : <ChevronRight className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0">
                        <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-ink-subtle" />
                            Date surcharges
                            <span className="text-xs font-normal text-ink-subtle">
                                ({surcharges.length})
                            </span>
                        </h2>
                        <p className="text-xs text-ink-subtle mt-0.5 truncate">
                            {expanded
                                ? 'Percentage multiplier applied on specific dates (e.g. 100% on 25 Dec). Multiplies the base fare ex VAT after discounts and flat rates.'
                                : surcharges.length === 0
                                    ? 'No surcharges yet — click to expand.'
                                    : surcharges.map(s => `${formatDate(s.surcharge_date)} +${Number(s.surcharge_pct).toFixed(0)}%`).join(' · ')}
                        </p>
                    </div>
                </button>
                {expanded && (
                    <Button size="sm" onClick={() => setShowAddModal(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        Add surcharge
                    </Button>
                )}
            </div>

            {expanded && (
                <>
                    {isLoading && <div className="card-body text-sm text-ink-subtle text-center py-6">Loading surcharges…</div>}
                    {isError && <div className="card-body"><ErrorPage message={error?.message} onRetry={refetch} /></div>}
                    {!isLoading && !isError && surcharges.length === 0 && (
                        <div className="card-body text-sm text-ink-subtle text-center py-6">
                            No surcharges yet. Add one to charge extra on specific dates.
                        </div>
                    )}
                    {!isLoading && !isError && surcharges.length > 0 && (
                        <ul className="divide-y divide-paper-border-soft">
                            {surcharges.map((s) => (
                                <DateSurchargeRow
                                    key={s.id}
                                    surcharge={s}
                                    onEdit={() => setEditTarget(s)}
                                    onDelete={() => setDeleteTarget(s)}
                                />
                            ))}
                        </ul>
                    )}
                </>
            )}

            {showAddModal && (
                <DateSurchargeModal mode="add" open onClose={() => setShowAddModal(false)} onSaved={handleChanged} />
            )}
            {editTarget && (
                <DateSurchargeModal mode="edit" surcharge={editTarget} open onClose={() => setEditTarget(null)} onSaved={handleChanged} />
            )}
            {deleteTarget && (
                <DeleteDateSurchargeModal surcharge={deleteTarget} open onClose={() => setDeleteTarget(null)} onDeleted={handleChanged} />
            )}
        </div>
    );
}


function DateSurchargeRow({ surcharge, onEdit, onDelete }) {
    const customers = surcharge.customers || [];
    const appliesToAll = surcharge.applies_to_all !== false;

    function renderScope() {
        if (appliesToAll && customers.length === 0) {
            return <span className="text-ink-subtle">All customers</span>;
        }
        const names = customers.map((c) =>
            customerDisplayName({
                company_name: c.company_name,
                first_name: c.first_name,
                last_name: c.last_name,
            })
        );
        const shown = names.slice(0, 4);
        const overflow = names.length - shown.length;
        return (
            <span>
                <span className="text-ink-subtle">
                    {appliesToAll ? 'All customers except ' : 'Only '}
                </span>
                {shown.map((n, i) => (
                    <span key={i}>
                        {i > 0 && ', '}
                        <span className="text-ink">{n}</span>
                    </span>
                ))}
                {overflow > 0 && <span className="text-ink-subtle"> + {overflow} more</span>}
            </span>
        );
    }

    return (
        <li className="px-6 py-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-ink flex-wrap">
                        <span>{surcharge.name}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                            +{Number(surcharge.surcharge_pct).toFixed(0)}%
                        </span>
                        {!surcharge.active && <StatusPill status="neutral">Inactive</StatusPill>}
                        {surcharge.apply_to_booking_engine && <StatusPill status="info">Public</StatusPill>}
                    </div>
                    <div className="mt-1 text-xs text-ink-subtle">
                        {formatDate(surcharge.surcharge_date)}
                        {' · '}
                        {renderScope()}
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button type="button" onClick={onEdit} className="p-1.5 text-ink-muted hover:text-ink hover:bg-paper-hover/50 rounded transition-colors" title="Edit">
                        <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={onDelete} className="p-1.5 text-ink-muted hover:text-status-danger hover:bg-paper-hover/50 rounded transition-colors" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </li>
    );
}


function DateSurchargeModal({ mode, surcharge, open, onClose, onSaved }) {
    const api = useApi();

    const [name, setName] = useState(surcharge?.name || '');
    const [surchargeDate, setSurchargeDate] = useState(
        surcharge?.surcharge_date
            ? new Date(surcharge.surcharge_date).toISOString().slice(0, 10)
            : ''
    );
    const [pct, setPct] = useState(surcharge ? String(surcharge.surcharge_pct) : '');
    const [active, setActive] = useState(surcharge?.active !== false);
    const [appliesToAll, setAppliesToAll] = useState(
        surcharge?.applies_to_all !== undefined ? surcharge.applies_to_all : true
    );
    const [applyToBookingEngine, setApplyToBookingEngine] = useState(
        surcharge?.apply_to_booking_engine === true
    );
    const [selectedCustomers, setSelectedCustomers] = useState(() =>
        (surcharge?.customers || []).map((c) => ({
            id: c.customer_id,
            company_name: c.company_name,
            first_name: c.first_name,
            last_name: c.last_name,
        }))
    );

    const [nameError, setNameError] = useState(null);
    const [dateError, setDateError] = useState(null);
    const [pctError, setPctError] = useState(null);

    const mutation = useMutation({
        mutationFn: () => {
            const customerIds = selectedCustomers.map((c) => c.id);
            if (mode === 'add') {
                return api.post('/pricing/admin/surcharges', {
                    name: name.trim(),
                    surcharge_date: surchargeDate,
                    surcharge_pct: parseFloat(pct),
                    applies_to_all: appliesToAll,
                    apply_to_booking_engine: applyToBookingEngine,
                    active,
                    customer_ids: customerIds,
                });
            }
            return api.patch(`/pricing/admin/surcharges/${surcharge.id}`, {
                name: name.trim(),
                surcharge_pct: parseFloat(pct),
                applies_to_all: appliesToAll,
                apply_to_booking_engine: applyToBookingEngine,
                active,
                customer_ids: customerIds,
            });
        },
        onSuccess: () => {
            toast.success(mode === 'add' ? 'Surcharge created' : 'Surcharge updated');
            onSaved();
        },
        onError: (err) => {
            const apiErr = err?.response?.error || err?.body?.error;
            if (apiErr === 'date_already_exists') {
                setDateError('A surcharge already exists for that date. Edit the existing one instead.');
                return;
            }
            toast.error(err.message || 'Could not save surcharge');
        },
    });

    function handleSave() {
        setNameError(null);
        setDateError(null);
        setPctError(null);

        if (!name.trim()) { setNameError('Name is required.'); return; }
        if (mode === 'add' && !surchargeDate) { setDateError('Set a date.'); return; }
        const n = parseFloat(pct);
        if (isNaN(n) || n <= 0 || n > 500) {
            setPctError('Enter a percentage between 0.01 and 500.');
            return;
        }

        mutation.mutate();
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={mode === 'add' ? 'Add date surcharge' : 'Edit surcharge'}
            description="Percentage added to the base fare ex VAT on the specified date. Applied after discount and flat rates."
            size="md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                        Name <span className="text-status-danger">*</span>
                    </label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Christmas Day, NYE, Easter Sunday"
                    />
                    {nameError && <p className="mt-1 text-xs text-status-danger">{nameError}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Date <span className="text-status-danger">*</span>
                        </label>
                        <Input
                            type="date"
                            value={surchargeDate}
                            onChange={(e) => setSurchargeDate(e.target.value)}
                            disabled={mode === 'edit'}
                        />
                        {dateError && <p className="mt-1 text-xs text-status-danger">{dateError}</p>}
                        {mode === 'edit' && (
                            <p className="mt-1 text-xs text-ink-subtle">
                                Date can't be changed. Delete + recreate to move to a different date.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Surcharge % <span className="text-status-danger">*</span>
                        </label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max="500"
                            value={pct}
                            onChange={(e) => setPct(e.target.value)}
                            placeholder="e.g. 100 = double price"
                        />
                        {pctError && <p className="mt-1 text-xs text-status-danger">{pctError}</p>}
                    </div>
                </div>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Active</div>
                        <div className="text-xs text-ink-subtle">
                            Uncheck to disable without deleting.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={applyToBookingEngine}
                        onChange={(e) => setApplyToBookingEngine(e.target.checked)}
                        className="mt-0.5"
                    />
                    <div>
                        <div className="text-sm text-ink">Apply to booking engine</div>
                        <div className="text-xs text-ink-subtle">
                            When on, this surcharge also applies to anonymous bookings via book.lanzcts.com.
                        </div>
                    </div>
                </label>

                <div className="pt-3 border-t border-paper-border-soft space-y-3">
                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            Who this surcharge applies to
                        </label>
                        <div className="space-y-1.5">
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="surcharge_applies_to_all"
                                    checked={appliesToAll}
                                    onChange={() => setAppliesToAll(true)}
                                    className="mt-0.5"
                                />
                                <div>
                                    <div className="text-sm text-ink">All customers</div>
                                    <div className="text-xs text-ink-subtle">
                                        Default. Optionally exclude specific customers below.
                                    </div>
                                </div>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="surcharge_applies_to_all"
                                    checked={!appliesToAll}
                                    onChange={() => setAppliesToAll(false)}
                                    className="mt-0.5"
                                />
                                <div>
                                    <div className="text-sm text-ink">Only specific customers</div>
                                    <div className="text-xs text-ink-subtle">
                                        Allow-list. Surcharge only fires for customers added below.
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-ink-subtle uppercase tracking-wider font-medium mb-1.5">
                            {appliesToAll ? 'Customers to exclude' : 'Customers to include'}
                        </label>
                        <p className="text-xs text-ink-subtle mb-2">
                            {appliesToAll
                                ? 'Leave empty to apply to everyone. Add customers here to exempt them (e.g. corporate contracts).'
                                : 'Add at least one customer. Surcharge only fires for these.'}
                        </p>
                        <CustomerMultiPicker
                            selected={selectedCustomers}
                            onChange={setSelectedCustomers}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} loading={mutation.isPending}>
                    {mode === 'add' ? <><Plus className="h-4 w-4" />Create</> : 'Save changes'}
                </Button>
            </div>
        </Modal>
    );
}


function DeleteDateSurchargeModal({ surcharge, open, onClose, onDeleted }) {
    const api = useApi();
    const mutation = useMutation({
        mutationFn: () => api.delete(`/pricing/admin/surcharges/${surcharge.id}`),
        onSuccess: () => { toast.success('Surcharge deleted'); onDeleted(); },
        onError: (err) => toast.error(err.message || 'Could not delete surcharge'),
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Delete surcharge?"
            description="The surcharge will no longer apply on this date."
            size="md"
        >
            <div className="bg-paper-hover/50 rounded-md p-3 text-sm text-ink-muted">
                <div className="font-medium text-ink">
                    {surcharge.name} — +{Number(surcharge.surcharge_pct).toFixed(0)}%
                </div>
                <div className="mt-0.5 text-xs">{formatDate(surcharge.surcharge_date)}</div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="danger" onClick={() => mutation.mutate()} loading={mutation.isPending}>
                    <Trash2 className="h-4 w-4" /> Delete
                </Button>
            </div>
        </Modal>
    );
}


// =============================================================================
// Utilities
// =============================================================================
// Groups consecutive day-of-week windows with identical times. E.g.
//   [Mon 07-19, Tue 07-19, Wed 07-19] → "Mon–Wed 07:00–19:00"
function formatTimeWindows(windows) {
    if (!windows || windows.length === 0) return '';
    // Sort Mon-first (1..6, then 0=Sun at end) to match the editor.
    const order = [1, 2, 3, 4, 5, 6, 0];
    const labels = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 0: 'Sun' };
    const sorted = [...windows].sort((a, b) => order.indexOf(a.day_of_week) - order.indexOf(b.day_of_week));

    const groups = [];
    for (const w of sorted) {
        const last = groups[groups.length - 1];
        const isConsecutive =
            last &&
            last.time_from === w.time_from &&
            last.time_until === w.time_until &&
            order.indexOf(w.day_of_week) === order.indexOf(last.last_dow) + 1;
        if (isConsecutive) {
            last.last_dow = w.day_of_week;
        } else {
            groups.push({
                first_dow: w.day_of_week,
                last_dow: w.day_of_week,
                time_from: w.time_from,
                time_until: w.time_until,
            });
        }
    }
    return groups
        .map((g) => {
            const range = g.first_dow === g.last_dow
                ? labels[g.first_dow]
                : `${labels[g.first_dow]}–${labels[g.last_dow]}`;
            return `${range} ${g.time_from}–${g.time_until}`;
        })
        .join(' · ');
}

function formatDate(d) {
    if (!d) return '';
    try {
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return String(d);
    }
}
